// =============================================================================
// Lead scoring + quality flags for the Entrepreneur Application Funnel
// -----------------------------------------------------------------------------
// scoreApplication(answers) -> { score, tier, breakdown, flags }
// Pure functions — safe to run on the client or to re-use on a backend.
// =============================================================================

import { SCORE_WEIGHTS, TIER, TIER_THRESHOLDS } from './funnelConfig';

// Words that signal an entrepreneurial / business mindset in free-text answers.
const ENTREPRENEURIAL_KEYWORDS = [
  'business', 'sales', 'sell', 'selling', 'client', 'clients', 'customer', 'grow',
  'growth', 'income', 'build', 'building', 'commission', 'entrepreneur', 'invest',
  'goal', 'goals', 'market', 'marketing', 'referral', 'referrals', 'network',
  'networking', 'revenue', 'freelance', 'self-employed', 'manage', 'scale', 'brand',
  'profit', 'ownership', 'drive', 'driven', 'ambition', 'ambitious', 'discipline',
  'results', 'outreach', 'follow up', 'follow-up', 'prospect', 'pipeline',
];

// Phrases that signal a focus on travel PERKS rather than building a business.
const PERKS_PHRASES = [
  'free trip', 'free trips', 'free travel', 'discount', 'discounts', 'discounted',
  'travel perks', 'perks', 'get paid to travel', 'cheap travel', 'travel benefits',
  'travel for free',
];

// Phrases that signal a casual / HOBBY motivation with no business intent.
const HOBBY_PHRASES = [
  'love travel', 'love to travel', 'love traveling', 'love travelling',
  'passion for travel', 'always wanted to travel', 'i love vacations',
  'love vacations', 'hobby', 'just love travel', 'enjoy travelling',
  'enjoy traveling', 'love exploring',
];

function combinedText(whyInterested, successReason) {
  return `${whyInterested || ''} ${successReason || ''}`.toLowerCase().trim();
}

function wordCount(text) {
  return text ? text.split(/\s+/).filter(Boolean).length : 0;
}

function countHits(text, phrases) {
  return phrases.filter((p) => text.includes(p)).length;
}

/**
 * Heuristic scoring of the two written motivation answers.
 * Strong, business-focused answers earn points; thin "I just love travel"
 * answers lose points.
 */
export function scoreMotivation(whyInterested, successReason) {
  const text = combinedText(whyInterested, successReason);
  const words = wordCount(text);
  const keywordHits = countHits(text, ENTREPRENEURIAL_KEYWORDS);
  const casualHits = countHits(text, PERKS_PHRASES) + countHits(text, HOBBY_PHRASES);

  // Strong: substantive answer that talks about building a business.
  if (words >= 35 && keywordHits >= 3) {
    return { points: SCORE_WEIGHTS.strongMotivation, label: 'Strong, business-focused written answers' };
  }
  // Weak: short answer that only mentions loving travel / perks.
  if (casualHits > 0 && keywordHits === 0 && words < 40) {
    return { points: SCORE_WEIGHTS.weakMotivation, label: 'Casual answer focused only on loving travel' };
  }
  return { points: 0, label: 'Written answers — neutral' };
}

/**
 * Derive automatic lead-quality flags from a completed application.
 * Returns an array of flag keys (see LEAD_FLAGS in funnelConfig.js).
 */
export function computeApplicationFlags(a = {}) {
  const flags = [];
  const text = combinedText(a.whyInterested, a.successReason);
  const words = wordCount(text);
  const keywordHits = countHits(text, ENTREPRENEURIAL_KEYWORDS);
  const perksHits = countHits(text, PERKS_PHRASES);
  const hobbyHits = countHits(text, HOBBY_PHRASES);

  const strongWritten = words >= 35 && keywordHits >= 3;
  // The written answers are OPTIONAL on the form. Leaving them blank says
  // nothing about the applicant, so no written-answer flag — positive or
  // negative — may be derived from an empty response. Without this guard every
  // applicant who skipped the optional boxes would be flagged 'weak_motivation'
  // purely for skipping them.
  const hasWrittenAnswers = words > 0;

  // --- Positive signals ---
  if (a.salesExperience === 'yes') flags.push('strong_sales_background');
  if (a.businessExperience === 'yes') flags.push('entrepreneurial_background');
  if (strongWritten) flags.push('strong_written_answers');

  // --- Caution signals ---
  if (perksHits > 0) flags.push('travel_perks_focus');
  if (hobbyHits > 0 && keywordHits === 0) flags.push('hobby_language');
  // Weak motivation: thin answers, or casual answers with no business language.
  if (
    hasWrittenAnswers &&
    !strongWritten &&
    (words < 20 || (keywordHits === 0 && (perksHits > 0 || hobbyHits > 0)))
  ) {
    flags.push('weak_motivation');
  }
  if (a.hoursPerWeek === 'lt-10') flags.push('low_time_commitment');
  if (a.goalType === 'job' || a.understandsCommission === 'no') flags.push('wants_salary');
  if (a.comfortableLeads === 'no') flags.push('avoids_lead_generation');

  return flags;
}

const GOAL_LABELS = {
  business: 'Looking for a business opportunity',
  'side-hustle': 'Looking for a side hustle',
  job: 'Looking for a traditional job',
};

/**
 * Score a completed application.
 * @param {object} a - the form answers
 * @returns {{ score:number, tier:string, breakdown:Array<{label,points}>, flags:string[] }}
 */
export function scoreApplication(a = {}) {
  const breakdown = [];
  let score = 0;

  const add = (label, points) => {
    score += points;
    breakdown.push({ label, points });
  };

  add(GOAL_LABELS[a.goalType] || 'Goal selection', SCORE_WEIGHTS.goalType[a.goalType] || 0);
  add('Sales / commission-based experience', SCORE_WEIGHTS.salesExperience[a.salesExperience] || 0);
  add('Business / freelance experience', SCORE_WEIGHTS.businessExperience[a.businessExperience] || 0);
  add('Comfortable generating own leads', SCORE_WEIGHTS.comfortableLeads[a.comfortableLeads] || 0);
  add('Weekly time commitment', SCORE_WEIGHTS.hoursPerWeek[a.hoursPerWeek] || 0);
  add('Income ambition', SCORE_WEIGHTS.incomeGoal[a.incomeGoal] || 0);
  add('Understands commission-based model', SCORE_WEIGHTS.understandsCommission[a.understandsCommission] || 0);

  const motivation = scoreMotivation(a.whyInterested, a.successReason);
  add(motivation.label, motivation.points);

  const tier =
    score >= TIER_THRESHOLDS.qualified
      ? TIER.QUALIFIED
      : score >= TIER_THRESHOLDS.nurture
      ? TIER.NURTURE
      : TIER.NOT_FIT;

  return { score, tier, breakdown, flags: computeApplicationFlags(a) };
}
